import { useMemo, useState } from 'react';
import { Bell, AtSign, UserPlus, Flame, MessageSquare } from 'lucide-react';
import { useNotifications, type NotifKind } from '@/lib/notifications';
import type { Ticket } from '@/lib/mock';
import { cn } from '@/lib/utils';

function ago(at: number): string {
  const m = Math.round((Date.now() - at) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const ICON: Record<NotifKind, typeof Bell> = {
  assign: UserPlus,
  mention: AtSign,
  breach: Flame,
  comment: MessageSquare,
};

export function NotificationBell({ tickets, now }: { tickets: Ticket[]; now: number }) {
  const { notifs, unread, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);

  const breaching = useMemo(
    () => tickets.filter((t) => now > t.slaDueAt).slice(0, 6),
    [tickets, now],
  );

  const dot = unread > 0 || breaching.length > 0;

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen((o) => !o);
          if (!open) markAllRead();
        }}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:text-foreground"
        aria-label="Notifications"
      >
        <Bell className="h-[18px] w-[18px]" />
        {dot && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary ring-2 ring-card" />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
            <div className="border-b border-border px-4 py-2.5 text-sm font-semibold">Notifications</div>
            <div className="max-h-96 overflow-y-auto">
              {breaching.length > 0 && (
                <div>
                  <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-temp-ember">
                    Breaching now
                  </div>
                  {breaching.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                      <Flame className="h-4 w-4 flex-shrink-0 text-temp-ember" />
                      <span className="font-mono text-[11px] text-muted-foreground">{t.ref}</span>
                      <span className="flex-1 truncate">{t.subject}</span>
                    </div>
                  ))}
                </div>
              )}

              {notifs.length > 0 && (
                <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Activity
                </div>
              )}
              {notifs.map((n) => {
                const Icon = ICON[n.kind];
                return (
                  <div key={n.id} className={cn('flex items-start gap-2.5 px-4 py-2 text-sm', !n.read && 'bg-primary/[0.04]')}>
                    <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <div className="flex-1">
                      <div>{n.text}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{ago(n.at)}</div>
                    </div>
                  </div>
                );
              })}

              {notifs.length === 0 && breaching.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
